import { isMobileBrowser } from "common/utils/browser";
import { on as onEvent } from "common/utils/ui/event";
import { track } from "common/utils/mixpanel/tracker";
import { track as trackRudderstack } from "common/utils/rudderstack/tracker";
import "components/jiffy/brands/common/brand-products/brand-products";
import "components/jiffy/brands/public/brands-public-content/brands-public-content";
import "components/jiffy/brands/public/brands-public-cover/brands-public-cover";
import initBrandAllProducts from "components/jiffy/brands/common/brand-all-products/brand-all-products";
import initBrandFollow from "components/jiffy/brands/common/brand-follow/brand-follow";
import initBrandsPublicContentMenu from "components/jiffy/brands/public/brands-public-content-menu/brands-public-content-menu";
import initPageInfo from "components/jiffy/brands/public/brands-public-page-info/brands-public-page-info";
import initVideoPlayer from "./brands-video-player";
import "./component.scss";

class BrandsPublishedPage {
  constructor() {
    this.triggers = {
      followButton: "brand-page-follow-button",
      navItem: ".js-published-brand-page-nav-item",
      productLink: ".js-brand-product-item",
      viewAllProductsLink: ".js-brand-products-see-all"
    };

    this.deviceMode = isMobileBrowser() ? "mobile" : "desktop";

    this.assetIdentifierMap = {
      ".js-published-brand-primary-banner": "primary-banner",
      ".js-published-brand-left-banner": "left-banner",
      ".js-published-brand-right-banner": "right-banner",
      ".js-published-brand-cover": "cover",
      ".js-published-brand-logo": "logo"
    };
  }

  init() {
    this.trackNavChange(this.triggers.navItem, "brand_storefront_navbar_item_clicked");
    this.trackViewAllProducts(
      this.triggers.viewAllProductsLink,
      "brand_storefront_see_all_products_clicked"
    );
    this.trackBrandPageLinks(
      Object.keys(this.assetIdentifierMap),
      "brand_storefront_hyperlink_clicked"
    );
    this.trackProductClicks(this.triggers.productLink, "brand_storefront_see_product_clicked");
    this.trackFollow(this.triggers.followButton, "brand_storefront_follow_clicked");
  }

  get mixpanelProperties() {
    return {
      brand_name: window?.jiffy?.brands?.brandName,
      slug: window?.jiffy?.brands?.currentSlug,
      device_mode: this.deviceMode
    };
  }

  trackEvent(eventName, extraProperties) {
    try {
      const eventProperties = { ...extraProperties, ...this.mixpanelProperties };
      track(eventName, eventProperties);
    } catch (error) {
      window.Honeybadger.notify("Error occurred while tracking on mixpanel", {
        params: { error: JSON.stringify(error) }
      });
    }
  }

  trackNavChange(elementSelector, eventName) {
    onEvent("click", elementSelector, (_e, $target) => {
      if ($target.dataset.slug === window.jiffy.brands.currentSlug) return;

      this.trackEvent(eventName, {
        new_slug: $target.dataset.slug
      });
      trackRudderstack("Navigation Clicked", {
        destination: $target.href,
        is_app: Boolean(window.MOBILE_APP),
        location: "brands_storefront",
        platform: window.MOBILE_APP_PLATFORM || "web",
        text: $target.textContent?.trim(),
        type: "link",
        url: window.location.href,
        nav_type: "brand_storefront_navbar"
      });
    });
  }

  trackFollow(elementId, eventName) {
    const $element = document.getElementById(elementId);
    $element?.addEventListener("click", () => {
      this.trackEvent(eventName, {
        action: $element.innerText.trim() === "Follow" ? "Follow" : "Unfollow"
      });
    });
  }

  trackViewAllProducts(elementSelector, eventName) {
    onEvent("click", elementSelector, () => {
      this.trackEvent(eventName, {});
    });
  }

  trackBrandPageLinks(elementSelectors, eventName) {
    elementSelectors.forEach(elementSelector => {
      onEvent("click", elementSelector, (_event, $target) => {
        const imgSrc =
          $target.querySelector("img")?.src || $target.querySelector("video")?.currentSrc;
        const linkHref = $target.href;
        if (!imgSrc) return;
        const assetIdentifier = this.assetIdentifierMap[elementSelector];
        if (!assetIdentifier) return;
        const extraProperties = {
          link: linkHref,
          image_link: imgSrc,
          asset_identifier: assetIdentifier
        };
        this.trackEvent(eventName, extraProperties);
      });
    });
  }

  trackProductClicks(elementSelector, eventName) {
    onEvent("click", elementSelector, (_event, $target) => {
      const productLink = $target.querySelector("a")?.href;
      if (productLink) {
        this.trackEvent(eventName, { product_link: productLink });
      }
    });
  }
}

export default () => {
  new BrandsPublishedPage().init();
  initBrandAllProducts();
  initBrandFollow();
  initBrandsPublicContentMenu();
  initPageInfo();
  initVideoPlayer();
};
